import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Check, X, AlertCircle } from "lucide-react";

const ESTADO_BADGE = {
  pendiente:  { label: "Pendiente",  bg: "#fef3c7", color: "#92400e" },
  aprobado:   { label: "Aprobado",   bg: "#dcfce7", color: "#166534" },
  rechazado:  { label: "Rechazado",  bg: "#fee2e2", color: "#991b1b" },
};

export default function RevisionPDD() {
  const navigate = useNavigate();
  const [secretarias, setSecretarias]       = useState([]);
  const [expandida, setExpandida]           = useState(null);
  const [registros, setRegistros]           = useState({});
  const [loadingDep, setLoadingDep]         = useState({});
  const [accionando, setAccionando]         = useState({});
  const [observaciones, setObservaciones]   = useState({});
  const [mostrarObs, setMostrarObs]         = useState({});
  const [error, setError]                   = useState("");
  const [user, setUser]                     = useState(null);

  // ── Cargar lista de secretarías únicas ─────────────────────────
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("auth_user") || "{}");
    setUser(u);

    const fetchSecretarias = async () => {
      try {
        const resp = await fetch(
          "https://desarrollandoterritorios.com/api/pdd_secretarias.php",
          { credentials: "include" }
        );
        const json = await resp.json();
        if (json.ok) setSecretarias(json.data || []);
        else setError(json.message || "Error cargando secretarías");
      } catch (e) {
        setError("No se pudo conectar con el servidor");
      }
    };
    fetchSecretarias();
  }, []);

  // ── Cargar registros de una secretaría al expandir ──────────────
  const cargarRegistros = useCallback(async (dependencia) => {
    if (registros[dependencia]) return; // ya cargado
    setLoadingDep((p) => ({ ...p, [dependencia]: true }));
    try {
      const resp = await fetch(
        `https://desarrollandoterritorios.com/api/pdd_registros_revision.php?dependencia=${encodeURIComponent(dependencia)}`,
        { credentials: "include" }
      );
      const json = await resp.json();
      if (json.ok) {
        setRegistros((p) => ({ ...p, [dependencia]: json.data || [] }));
      } else {
        setRegistros((p) => ({ ...p, [dependencia]: [] }));
      }
    } catch {
      setRegistros((p) => ({ ...p, [dependencia]: [] }));
    } finally {
      setLoadingDep((p) => ({ ...p, [dependencia]: false }));
    }
  }, [registros]);

  const toggleSecretaria = (dep) => {
    if (expandida === dep) {
      setExpandida(null);
    } else {
      setExpandida(dep);
      cargarRegistros(dep);
    }
  };

  // ── Acción aprobar / rechazar ───────────────────────────────────
  const ejecutarAccion = async (registroId, accion, dependencia) => {
    const obs = observaciones[registroId] || "";
    if (accion === "rechazar" && !obs.trim()) {
      alert("Debe ingresar una observación para rechazar el registro.");
      return;
    }

    setAccionando((p) => ({ ...p, [registroId]: true }));
    try {
      const resp = await fetch(
        "https://desarrollandoterritorios.com/api/pdd_revisar_seguimiento.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            seguimiento_id: registroId,
            accion,
            observacion: obs,
            revisado_por: user?.id || user?.user_id,
          }),
        }
      );
      const json = await resp.json();
      if (json.ok) {
        // Actualizar estado local sin recargar todo
        setRegistros((prev) => ({
          ...prev,
          [dependencia]: prev[dependencia].map((r) =>
            r.id === registroId
              ? { ...r, estado: json.estado, observacion_rechazo: obs }
              : r
          ),
        }));
        setMostrarObs((p) => ({ ...p, [registroId]: false }));
        setObservaciones((p) => ({ ...p, [registroId]: "" }));
      } else {
        alert(json.message || "Error al procesar la acción");
      }
    } catch {
      alert("No se pudo conectar con el servidor");
    } finally {
      setAccionando((p) => ({ ...p, [registroId]: false }));
    }
  };

  // ── Contadores por secretaría ───────────────────────────────────
  const contarPendientes = (dep) => {
    return (registros[dep] || []).filter((r) => r.estado === "pendiente").length;
  };

  return (
    <div style={s.page}>
      <div style={s.wrapper}>

        {/* Header */}
        <div style={s.header}>
          <button onClick={() => navigate("/menu")} style={s.backBtn}>
            ← Regresar al menú
          </button>
          <h1 style={s.title}>Revisión de Seguimiento PDD</h1>
          <p style={s.subtitle}>Aprueba o devuelve los registros enviados por cada secretaría</p>
        </div>

        {error && (
          <div style={s.errorBox}>
            <AlertCircle size={16} style={{ marginRight: 8 }} />
            {error}
          </div>
        )}

        {/* Lista de secretarías */}
        <div style={s.lista}>
          {secretarias.length === 0 && !error && (
            <div style={s.empty}>Cargando secretarías...</div>
          )}

          {secretarias.map((sec) => {
            const dep       = sec.dependencia;
            const abierta   = expandida === dep;
            const pendientes = contarPendientes(dep);
            const items     = registros[dep] || [];

            return (
              <div key={dep} style={s.seccionCard}>

                {/* Cabecera secretaría */}
                <button
                  style={s.seccionHeader}
                  onClick={() => toggleSecretaria(dep)}
                >
                  <div style={s.seccionLeft}>
                    {abierta
                      ? <ChevronDown size={18} />
                      : <ChevronRight size={18} />
                    }
                    <span style={s.seccionNombre}>{dep}</span>
                    {pendientes > 0 && (
                      <span style={s.badgePendiente}>{pendientes} pendiente{pendientes > 1 ? "s" : ""}</span>
                    )}
                  </div>
                  <span style={s.seccionMeta}>
                    {sec.total_indicadores} indicador{sec.total_indicadores !== 1 ? "es" : ""}
                  </span>
                </button>

                {/* Registros desplegados */}
                {abierta && (
                  <div style={s.seccionBody}>
                    {loadingDep[dep] && (
                      <div style={s.empty}>Cargando registros...</div>
                    )}

                    {!loadingDep[dep] && items.length === 0 && (
                      <div style={s.empty}>No hay registros de seguimiento para esta secretaría.</div>
                    )}

                    {!loadingDep[dep] && items.map((reg) => {
                      const badge     = ESTADO_BADGE[reg.estado] || ESTADO_BADGE.pendiente;
                      const cargando  = accionando[reg.id];
                      const showObs   = mostrarObs[reg.id];

                      return (
                        <div key={reg.id} style={s.regCard}>
                          <div style={s.regTop}>
                            <div style={s.regInfo}>
                              <div style={s.regIndicador}>{reg.numero_indicador} — {reg.nombre_indicador}</div>
                              <div style={s.regMeta}>
                                <span>Actividad: {reg.actividad}</span>
                                <span style={{ margin: "0 8px" }}>·</span>
                                <span>Fecha: {reg.fecha}</span>
                                <span style={{ margin: "0 8px" }}>·</span>
                                <span>Cantidad: {reg.cantidad_realizada}</span>
                                <span style={{ margin: "0 8px" }}>·</span>
                                <span>Tipo evidencia: {reg.tipo_evidencia || "—"}</span>
                              </div>
                              {reg.ubicacion_evidencia && (
                                <div style={s.regEvidencia}>
                                  Evidencia: <a href={reg.ubicacion_evidencia} target="_blank" rel="noreferrer" style={{ color: "#1677c8" }}>{reg.ubicacion_evidencia}</a>
                                </div>
                              )}
                              {reg.estado === "rechazado" && reg.observacion_rechazo && (
                                <div style={s.obsRechazo}>
                                  Motivo de rechazo: {reg.observacion_rechazo}
                                </div>
                              )}
                            </div>

                            <div style={s.regAcciones}>
                              <span style={{ ...s.estadoBadge, background: badge.bg, color: badge.color }}>
                                {badge.label}
                              </span>

                              {reg.estado !== "aprobado" && (
                                <>
                                  <button
                                    style={s.btnAprobar}
                                    disabled={cargando}
                                    onClick={() => ejecutarAccion(reg.id, "aprobar", dep)}
                                  >
                                    <Check size={14} /> Aprobar
                                  </button>

                                  <button
                                    style={s.btnRechazar}
                                    disabled={cargando}
                                    onClick={() =>
                                      setMostrarObs((p) => ({ ...p, [reg.id]: !p[reg.id] }))
                                    }
                                  >
                                    <X size={14} /> Devolver
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Panel de observación para rechazo */}
                          {showObs && (
                            <div style={s.obsPanel}>
                              <textarea
                                style={s.obsInput}
                                placeholder="Describe qué debe corregir la secretaría..."
                                rows={3}
                                value={observaciones[reg.id] || ""}
                                onChange={(e) =>
                                  setObservaciones((p) => ({ ...p, [reg.id]: e.target.value }))
                                }
                              />
                              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                                <button
                                  style={s.btnConfirmarRechazo}
                                  disabled={cargando}
                                  onClick={() => ejecutarAccion(reg.id, "rechazar", dep)}
                                >
                                  {cargando ? "Enviando..." : "Confirmar devolución"}
                                </button>
                                <button
                                  style={s.btnCancelar}
                                  onClick={() =>
                                    setMostrarObs((p) => ({ ...p, [reg.id]: false }))
                                  }
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const s = {
  page:             { minHeight: "100vh", background: "#f5f7fb", padding: "24px" },
  wrapper:          { maxWidth: "1200px", margin: "0 auto" },
  header:           { marginBottom: 24 },
  backBtn:          { border: "none", borderRadius: 10, background: "#1f3b64", color: "#fff", padding: "10px 14px", cursor: "pointer", fontWeight: 700, fontSize: 14, marginBottom: 14 },
  title:            { margin: 0, fontSize: 30, fontWeight: 800, color: "#1f3b64" },
  subtitle:         { marginTop: 6, color: "#5b6472", fontSize: 15 },
  errorBox:         { display: "flex", alignItems: "center", background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 14 },
  lista:            { display: "flex", flexDirection: "column", gap: 12 },
  empty:            { padding: "20px", textAlign: "center", color: "#64748b", fontSize: 14 },
  seccionCard:      { background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", overflow: "hidden" },
  seccionHeader:    { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left" },
  seccionLeft:      { display: "flex", alignItems: "center", gap: 10 },
  seccionNombre:    { fontSize: 16, fontWeight: 700, color: "#1f3b64" },
  seccionMeta:      { fontSize: 13, color: "#6b7280" },
  badgePendiente:   { fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 999, padding: "3px 8px", fontWeight: 600 },
  seccionBody:      { borderTop: "1px solid #f1f5f9", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 },
  regCard:          { background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", padding: "14px 16px" },
  regTop:           { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" },
  regInfo:          { flex: 1, minWidth: 0 },
  regIndicador:     { fontWeight: 700, color: "#1e293b", fontSize: 14, marginBottom: 4 },
  regMeta:          { fontSize: 12, color: "#64748b", display: "flex", flexWrap: "wrap", gap: 2 },
  regEvidencia:     { fontSize: 12, color: "#475569", marginTop: 4 },
  obsRechazo:       { fontSize: 12, color: "#991b1b", background: "#fee2e2", borderRadius: 6, padding: "6px 10px", marginTop: 6 },
  regAcciones:      { display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" },
  estadoBadge:      { fontSize: 12, padding: "4px 10px", borderRadius: 999, fontWeight: 600 },
  btnAprobar:       { display: "flex", alignItems: "center", gap: 4, border: "none", borderRadius: 8, background: "#16a34a", color: "#fff", padding: "8px 14px", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  btnRechazar:      { display: "flex", alignItems: "center", gap: 4, border: "none", borderRadius: 8, background: "#ef4444", color: "#fff", padding: "8px 14px", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  obsPanel:         { marginTop: 12, borderTop: "1px solid #e2e8f0", paddingTop: 12 },
  obsInput:         { width: "100%", borderRadius: 8, border: "1px solid #cbd5e1", padding: "10px 12px", fontSize: 13, resize: "vertical", boxSizing: "border-box", outline: "none" },
  btnConfirmarRechazo: { border: "none", borderRadius: 8, background: "#dc2626", color: "#fff", padding: "9px 16px", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  btnCancelar:      { border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", color: "#475569", padding: "9px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13 },
};